import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { axiosInstance } from "../axios";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

// TODO: The @/components/ui/* imports resolve via each frontend's bundler alias.
// Both frontends must configure their Vite alias so that @/components/ui/* resolves
// to their local shadcn/ui components when importing from @retro-ia/shared.

interface ActivateAccountResponse {
  message: string;
}

const validatePassword = (pwd: string): string | null => {
  if (pwd.length < 8)
    return "La contraseña debe tener al menos 8 caracteres";
  if (!/[A-Z]/.test(pwd))
    return "La contraseña debe contener al menos una mayúscula";
  if (!/[a-z]/.test(pwd))
    return "La contraseña debe contener al menos una minúscula";
  if (!/[0-9]/.test(pwd))
    return "La contraseña debe contener al menos un número";
  if (!/[@$!%*?&]/.test(pwd))
    return "La contraseña debe contener al menos un carácter especial (@$!%*?&)";
  return null;
};

export interface ActivateAccountPageProps {
  /** URL or imported image for the brand logo */
  logo?: string;
  /** Optional ThemeToggle component to render in the top-right corner */
  themeToggle?: React.ReactNode;
}

export function ActivateAccountPage({
  logo,
  themeToggle,
}: ActivateAccountPageProps = {}) {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (!token) {
      setError("Token de activación inválido");
      return;
    }

    setIsLoading(true);

    try {
      await axiosInstance.post<ActivateAccountResponse>("/auth/activate", {
        token,
        password,
      });

      setSuccess(true);

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } }).response?.data
          ?.message || "Error al activar la cuenta";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="flex items-center justify-center min-h-screen bg-gradient-to-b"
        style={{
          backgroundImage:
            "linear-gradient(to bottom, var(--linear-gradient-start), var(--linear-gradient-end))",
        }}
      >
        {themeToggle && (
          <div className="absolute top-4 right-4">{themeToggle}</div>
        )}
        <Card className="w-full max-w-md mx-4">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-center">¡Cuenta Activada!</CardTitle>
            <CardDescription className="text-center">
              Tu cuenta ha sido activada exitosamente. Serás redirigido al
              inicio de sesión...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="flex h-screen items-center justify-center bg-gradient-to-b"
      style={{
        backgroundImage:
          "linear-gradient(to bottom, var(--linear-gradient-start), var(--linear-gradient-end))",
      }}
    >
      {themeToggle && (
        <div className="absolute top-4 right-4">{themeToggle}</div>
      )}
      <div
        className="min-h-screen flex items-center justify-center px-3 sm:px-4 md:px-6 shadow-xl"
        style={{
          background: `radial-gradient(circle at top right, var(--linear-gradient-end), var(--linear-gradient-start) 40%)`,
        }}
      >
        <div className="min-h-screen w-full max-w-2xl flex items-start justify-center px-4 sm:px-8 md:px-12 lg:px-20 pt-[10vh]">
          <div className="flex flex-col w-full max-w-md">
            {logo && (
              <div className="m-6 sm:m-8 md:m-10 flex justify-center">
                <img
                  src={logo}
                  alt="Logo"
                  className="h-auto w-40 sm:w-50"
                />
              </div>
            )}

            <Card className="w-full">
              <CardHeader>
                <CardTitle>Activar tu cuenta</CardTitle>
                <CardDescription>
                  Establece una contraseña segura para completar la activación
                  de tu cuenta
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <XCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="password">Contraseña</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      required
                      disabled={isLoading}
                    />
                    <p className="text-sm text-muted-foreground">
                      Debe contener mayúsculas, minúsculas, números y caracteres
                      especiales
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      Confirmar Contraseña
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repite tu contraseña"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Activando cuenta...
                      </>
                    ) : (
                      "Activar cuenta"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
